import { Container, Truck } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { SellerProductQuery } from '~/graphql/graphql';
import { Input } from '~/components/ui';
import { toStandardUnits, unitsPerPiece } from '~/utils/unitConversion';
import { useTranslation } from 'react-i18next';

interface QuantitySelectorProps {
  sellerProduct: NonNullable<SellerProductQuery['sellerProduct']>;
  quantity: string;
  onQuantityChange: (quantity: string) => void;
}

function calcTransport(
  quantityInStdUnits: number,
  capacityPerUnit: number | null | undefined
) {
  if (
    capacityPerUnit == null ||
    capacityPerUnit === 0 ||
    quantityInStdUnits <= 0
  )
    return null;
  const loads = quantityInStdUnits / capacityPerUnit;
  const count = Math.ceil(loads);
  const lastFillPct = Math.round((loads % 1 || 1) * 100);
  return { count, loads: Math.round(loads * 100) / 100, lastFillPct };
}

export function QuantitySelector({
  sellerProduct,
  quantity,
  onQuantityChange,
}: Readonly<QuantitySelectorProps>) {
  const { t } = useTranslation('cart');
  const { minimumOrderQuantity } = sellerProduct;
  const unit = sellerProduct.unit ?? 'MBF';
  const { platformProduct, length } = sellerProduct.platformProductLength;
  const stdUnit = platformProduct.standardUnitsCode ?? unit;

  const widthInches = platformProduct.isNominal
    ? (platformProduct.nominalWidthInches ??
      platformProduct.actualWidthInches ??
      0)
    : (platformProduct.actualWidthInches ?? 0);
  const thicknessInches = platformProduct.isNominal
    ? (platformProduct.nominalThicknessInches ??
      platformProduct.actualThicknessInches ??
      0)
    : (platformProduct.actualThicknessInches ?? 0);
  const lengthInches = platformProduct.isNominal
    ? (length.nominalLengthInches ?? length.actualLengthInches)
    : (platformProduct.actualLengthInches ?? length.actualLengthInches);

  const [inputValue, setInputValue] = useState(quantity);

  useEffect(() => {
    setInputValue(quantity);
  }, [quantity]);

  const qty = Number(inputValue) || 0;
  const validDimensions =
    (lengthInches ?? 0) > 0 && widthInches > 0 && thicknessInches > 0;
  const stdQty = validDimensions
    ? toStandardUnits(
        qty,
        unit,
        stdUnit,
        lengthInches,
        widthInches,
        thicknessInches
      )
    : 0;

  const truck = calcTransport(stdQty, platformProduct.unitsPerTruckload.value);
  const railcars = calcTransport(stdQty, platformProduct.unitsPerRailCar.value);

  const unitsPerBundle = validDimensions
    ? platformProduct.piecesPerBundle.value *
      unitsPerPiece(unit, lengthInches, widthInches, thicknessInches)
    : 0;

  const step =
    unitsPerBundle > 0
      ? Math.round(Math.ceil(1 / unitsPerBundle) * unitsPerBundle * 1000) / 1000
      : 1;

  function roundToBundle(rawQty: number): number {
    if (unitsPerBundle <= 0 || rawQty <= 0) return rawQty;
    return Math.ceil(rawQty / unitsPerBundle) * unitsPerBundle;
  }

  return (
    <div className="bg-neut-0 rounded-lg border border-neutral-200 p-4">
      <div className="flex items-end gap-4">
        <Input
          label={`${t('cart.quantity')} (${unit})`}
          type="number"
          value={inputValue}
          onChange={(_, value) => {
            setInputValue(value);
            onQuantityChange(value);
          }}
          onBlur={() => {
            const rounded = roundToBundle(Number(inputValue) || 0).toFixed(3);
            setInputValue(rounded);
            onQuantityChange(rounded);
          }}
          name={'quantity'}
          step={step}
          className=""
          containerClassName="bg-neut-0"
        />
        <span className="text-sm text-neut-500 font-normal whitespace-nowrap pb-2">
          {t('cart.min')}: {minimumOrderQuantity.value} {unit}
        </span>
      </div>
      <div className="mt-4 pt-4 border-t border-neut-200">
        <p className="text-xs text-neut-600 font-medium mb-3">
          {t('cart.transportation_estimate')}
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-status-info-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Truck
                size={16}
                className="text-status-info-600"
                aria-hidden="true"
              />
              <span className="text-xs text-neut-600 font-normal">
                {t('cart.trucks')}
              </span>
            </div>
            {truck ? (
              <>
                <div className="flex items-baseline gap-1">
                  <span
                    data-testid="cart-trucks"
                    className="text-lg text-status-info-800 font-semibold"
                  >
                    {truck.count}
                  </span>
                  <span className="text-xs text-status-info-600 font-normal">
                    {t('cart.trucks_lowercase')}
                  </span>
                </div>
                <p
                  data-testid="cart-truck-loads"
                  className="text-xs text-neut-500 mt-1"
                >
                  {truck.loads} {t('cart.loads')}
                </p>
                <p className="text-xs text-status-info-600 mt-1">
                  {t('cart.last_truck')}: {truck.lastFillPct}% {t('cart.full')}
                </p>
              </>
            ) : (
              <p className="text-xs text-neut-500 mt-1">—</p>
            )}
          </div>
          <div className="bg-warning-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Container
                size={16}
                className="text-warning-600"
                aria-hidden="true"
              />
              <span className="text-xs text-neut-600 font-normal">
                {t('cart.railcars')}
              </span>
            </div>
            {railcars ? (
              <>
                <div className="flex items-baseline gap-1">
                  <span
                    data-testid="cart-railcars"
                    className="text-lg text-warning-700 font-semibold"
                  >
                    {railcars.count}
                  </span>
                  <span className="text-xs text-warning-600 font-normal">
                    {t('cart.railcars_lowercase')}
                  </span>
                </div>
                <p
                  data-testid="cart-railcar-loads"
                  className="text-xs text-neut-500 mt-1"
                >
                  {railcars.loads} {t('cart.loads')}
                </p>
                <p className="text-xs text-warning-600 mt-1">
                  {t('cart.last_railcar')}: {railcars.lastFillPct}%{' '}
                  {t('cart.full')}
                </p>
              </>
            ) : (
              <p className="text-xs text-neut-500 mt-1">—</p>
            )}
          </div>
        </div>
        <p className="text-xs text-neut-500 mt-2 italic">
          * {t('cart.estimate_description')}
        </p>
      </div>
    </div>
  );
}
